import PaperflyAPI from '../server/api/PaperflyAPI';
import { gae } from '../components/helpers/Analytics';

const pendingSourcePdfs = new Map();

export function stashPendingSourcePdf(documentId, file) {
  pendingSourcePdfs.set(documentId, file);
}

export function clearPendingSourcePdf(documentId) {
  gae('PENDING_SOURCE_PDF', 'REMOVE_SOURCE_PDF', documentId);
  pendingSourcePdfs.delete(documentId);
}

export function clearAllPendingSourcePdfs() {
  gae('PENDING_SOURCE_PDF', 'REMOVE_ALL_SOURCE_PDF');
  pendingSourcePdfs.clear();
}

export function hasPendingSourcePdfs() {
  return pendingSourcePdfs.size > 0;
}

/**
 * Snapshot for IndexedDB draft persistence.
 * @returns {{ documentId: string, blob: Blob, name: string, type: string }[]}
 */
export function snapshotPendingSourcePdfs() {
  const entries = [];
  for (const [documentId, file] of pendingSourcePdfs.entries()) {
    if (!file) continue;
    entries.push({
      documentId,
      blob: file,
      name: file.name || 'document.pdf',
      type: file.type || 'application/pdf'
    });
  }
  return entries;
}

/**
 * Restore stashed source PDFs after standalone draft load.
 * @param {{ documentId: string, blob: Blob, name?: string, type?: string }[]} entries
 */
export function restorePendingSourcePdfs(entries) {
  clearAllPendingSourcePdfs();
  if (!Array.isArray(entries) || !entries.length) return;

  entries.forEach(entry => {
    if (!entry?.documentId || !entry.blob) return;
    const file =
      typeof File !== 'undefined'
        ? new File([entry.blob], entry.name || 'document.pdf', {
            type: entry.type || entry.blob.type || 'application/pdf'
          })
        : entry.blob;
    pendingSourcePdfs.set(entry.documentId, file);
  });
  gae('PENDING_SOURCE_PDF', 'RESTORE_FROM_DRAFT', String(entries.length));
}

export async function flushPendingSourcePdfs(recipientRequestId, onUploaded) {
  gae('PENDING_SOURCE_PDF', 'FLUSH_AND_SUBMIT_SOURCE_PDFS');
  const entries = [...pendingSourcePdfs.entries()];

  for (const [documentId, file] of entries) {
    try {
      gae('PENDING_SOURCE_PDF', 'SUBMIT_SOURCE_PDF', documentId);
      const result = await PaperflyAPI.submitRequestedDocumentPdf(
        recipientRequestId,
        documentId,
        file
      );

      if (result?.success) {
        gae('PENDING_SOURCE_PDF', 'SUBMIT_SOURCE_PDF_SUCCESSFUL', documentId);
        onUploaded?.(documentId, file.name);
        pendingSourcePdfs.delete(documentId);
      } else {
        gae(
          'PENDING_SOURCE_PDF',
          'SUBMIT_SOURCE_PDF_ERROR',
          result?.errorMessage
        );
      }
    } catch (e) {
      gae('PENDING_SOURCE_PDF', 'SUBMIT_SOURCE_PDF_ERROR', JSON.stringify(e));
    }
  }
}

/**
 * On PDF pick: stash File in template mode (no recipientRequestId yet),
 * otherwise upload to S3 immediately.
 */
export function handleSourcePdfLoaded({
  templateMode,
  recipientRequestId,
  documentId,
  file,
  filesCount,
  setDocumentPdfUploaded,
  gaeCategory = 'UPLOADER'
}) {
  if (filesCount > 1) {
    gae(gaeCategory, 'IGNORE_SUBMIT_SOURCE_PDF_MANY_PDFS');
    return;
  }

  if (templateMode) {
    gae(gaeCategory, 'STASH_PENDING_SOURCE_PDF');
    stashPendingSourcePdf(documentId, file);
    setDocumentPdfUploaded?.(documentId, file.name);
    return;
  }

  gae(gaeCategory, 'SUBMIT_SOURCE_PDF');

  PaperflyAPI.submitRequestedDocumentPdf(recipientRequestId, documentId, file)
    .then(result => {
      if (result?.success) {
        gae(gaeCategory, 'SUBMIT_SOURCE_PDF_SUCCESSFUL');
        setDocumentPdfUploaded?.(documentId, file.name);
      } else {
        gae(gaeCategory, 'SUBMIT_SOURCE_PDF_ERROR', result?.errorMessage);
      }
    })
    .catch(e => {
      gae(gaeCategory, 'SUBMIT_SOURCE_PDF_ERROR', JSON.stringify(e));
    });
}
